import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import api, { naira } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const EMPTY = {
  name: "", category: "Face", price: "", salePrice: "", stock: "",
  description: "", ingredients: "", benefits: "", howToUse: "",
  images: [], isFeatured: false, isActive: true,
};

export default function ProductsAdmin() {
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/products").then((r) => setProducts(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (p) => {
    setForm({ ...EMPTY, ...p, salePrice: p.salePrice ?? "" });
    setEditId(p.id);
    setOpen(true);
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) return toast.error("Image must be under 3MB");
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, images: [...f.images, reader.result] }));
    reader.readAsDataURL(file);
  };

  const removeImage = (idx) => setForm((f) => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      price: Number(form.price),
      salePrice: form.salePrice === "" ? null : Number(form.salePrice),
      stock: Number(form.stock),
    };
    try {
      if (editId) await api.put(`/admin/products/${editId}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(editId ? "Product updated" : "Product created");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/admin/products/${id}`);
    toast.success("Product deleted");
    load();
  };

  return (
    <div data-testid="admin-products">
      <div className="flex justify-between items-center mb-6 md:mb-8 gap-3">
        <div className="min-w-0">
          <h1 className="font-serif text-2xl md:text-3xl text-espresso">Products</h1>
          <p className="text-bark text-sm">{products.length} products</p>
        </div>
        <Button onClick={openNew} data-testid="add-product-button" className="bg-terracotta text-bone hover:bg-espresso shrink-0">
          <Plus className="w-4 h-4 mr-1" /> <span className="hidden sm:inline">Add product</span><span className="sm:hidden">Add</span>
        </Button>
      </div>

      <div className="border border-sandline rounded-xl overflow-hidden">
        {/* Desktop header */}
        <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-3 bg-sand text-bark text-xs uppercase tracking-wide font-medium">
          <span className="col-span-5">Product</span>
          <span className="col-span-2">Category</span>
          <span className="col-span-2">Price</span>
          <span className="col-span-2">Stock</span>
          <span className="col-span-1 text-right">Actions</span>
        </div>
        {products.map((p) => (
          <div key={p.id} className="border-t border-sandline first:border-t-0 md:first:border-t" data-testid={`product-row-${p.id}`}>
            {/* Desktop row */}
            <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-3 items-center text-sm">
              <div className="col-span-5 flex items-center gap-3">
                <img src={p.images?.[0]} alt="" className="w-10 h-10 rounded object-cover bg-sand" />
                <span className="text-espresso font-medium">{p.name}</span>
                {!p.isActive && <span className="text-xs text-bark">(hidden)</span>}
              </div>
              <span className="col-span-2 text-bark">{p.category}</span>
              <span className="col-span-2 text-espresso">{naira(p.salePrice || p.price)}</span>
              <span className={`col-span-2 ${p.stock <= 5 ? "text-terracotta" : "text-bark"}`}>{p.stock}</span>
              <div className="col-span-1 flex justify-end gap-1">
                <button onClick={() => openEdit(p)} data-testid={`edit-product-${p.id}`} className="p-1.5 text-bark hover:text-espresso"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => del(p.id)} data-testid={`delete-product-${p.id}`} className="p-1.5 text-bark hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            {/* Mobile card */}
            <div className="md:hidden p-4 flex items-start gap-3">
              <img src={p.images?.[0]} alt="" className="w-16 h-16 rounded-lg object-cover bg-sand shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-espresso font-medium text-sm truncate">{p.name}</p>
                <p className="text-xs text-bark mt-0.5">
                  <span className="uppercase tracking-wide">{p.category}</span>
                  {!p.isActive && <span className="ml-2 italic">(hidden)</span>}
                </p>
                <div className="flex items-center gap-3 mt-2 text-sm">
                  <span className="text-espresso font-medium">{naira(p.salePrice || p.price)}</span>
                  <span className="text-bark">·</span>
                  <span className={p.stock <= 5 ? "text-terracotta" : "text-bark"}>Stock: {p.stock}</span>
                </div>
              </div>
              <div className="flex flex-col gap-1 shrink-0">
                <button onClick={() => openEdit(p)} data-testid={`edit-product-mobile-${p.id}`} aria-label="Edit" className="w-10 h-10 rounded-lg border border-sandline flex items-center justify-center text-bark active:bg-sand"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => del(p.id)} data-testid={`delete-product-mobile-${p.id}`} aria-label="Delete" className="w-10 h-10 rounded-lg border border-sandline flex items-center justify-center text-destructive active:bg-destructive/10"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-bone max-w-2xl w-[calc(100%-1rem)] max-h-[92vh] overflow-y-auto p-5 md:p-6" data-testid="product-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-espresso">{editId ? "Edit product" : "New product"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="text-sm text-bark mb-1 block">Name</label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="product-name-input" className="border-sandline" />
              </div>
              <div>
                <label className="text-sm text-bark mb-1 block">Category</label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="product-category-select" className="border-sandline"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Face">Face</SelectItem>
                    <SelectItem value="Body">Body</SelectItem>
                    <SelectItem value="Kits">Kits</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm text-bark mb-1 block">Stock</label>
                <Input type="number" required value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} data-testid="product-stock-input" className="border-sandline" />
              </div>
              <div>
                <label className="text-sm text-bark mb-1 block">Price (₦)</label>
                <Input type="number" required value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="product-price-input" className="border-sandline" />
              </div>
              <div>
                <label className="text-sm text-bark mb-1 block">Sale price (₦, optional)</label>
                <Input type="number" value={form.salePrice} onChange={(e) => setForm({ ...form, salePrice: e.target.value })} data-testid="product-saleprice-input" className="border-sandline" />
              </div>
            </div>

            <div>
              <label className="text-sm text-bark mb-1 block">Description</label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="border-sandline" rows={2} />
            </div>
            <div className="grid grid-cols-1 gap-4">
              <div><label className="text-sm text-bark mb-1 block">Benefits</label><Input value={form.benefits} onChange={(e) => setForm({ ...form, benefits: e.target.value })} className="border-sandline" /></div>
              <div><label className="text-sm text-bark mb-1 block">Ingredients</label><Textarea value={form.ingredients} onChange={(e) => setForm({ ...form, ingredients: e.target.value })} className="border-sandline" rows={2} /></div>
              <div><label className="text-sm text-bark mb-1 block">How to use</label><Textarea value={form.howToUse} onChange={(e) => setForm({ ...form, howToUse: e.target.value })} className="border-sandline" rows={2} /></div>
            </div>

            <div>
              <label className="text-sm text-bark mb-1 block">Images</label>
              <div className="flex flex-wrap gap-3">
                {form.images.map((img, i) => (
                  <div key={i} className="relative w-16 h-16">
                    <img src={img} alt="" className="w-full h-full object-cover rounded-lg" />
                    <button type="button" onClick={() => removeImage(i)} className="absolute -top-1.5 -right-1.5 bg-espresso text-bone rounded-full p-0.5"><X className="w-3 h-3" /></button>
                  </div>
                ))}
                <label className="w-16 h-16 border border-dashed border-sandline rounded-lg flex items-center justify-center cursor-pointer hover:border-terracotta">
                  <Upload className="w-5 h-5 text-bark" />
                  <input type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid="product-image-upload" />
                </label>
              </div>
            </div>

            <div className="flex gap-6">
              <label className="flex items-center gap-2 text-sm text-espresso">
                <Switch checked={form.isFeatured} onCheckedChange={(v) => setForm({ ...form, isFeatured: v })} data-testid="product-featured-switch" /> Featured
              </label>
              <label className="flex items-center gap-2 text-sm text-espresso">
                <Switch checked={form.isActive} onCheckedChange={(v) => setForm({ ...form, isActive: v })} data-testid="product-active-switch" /> Active
              </label>
            </div>

            <DialogFooter>
              <Button type="submit" disabled={saving} data-testid="save-product-button" className="bg-terracotta text-bone hover:bg-espresso">
                {saving ? "Saving…" : "Save product"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
